/**
 * NavmeshStatusChip — the viewport chip for a NOT-BUILT navmesh (ENG-1050).
 *
 * Shown only while the outliner's NAVMESH row is selected AND there is nothing to
 * walk on. That pairing is the point: selecting the row asks "show me the
 * navigation", and a built navmesh answers by DRAWING itself (NavMeshVolume's
 * overlay). When there is nothing to draw, an empty viewport would read as a
 * broken overlay, so the chip says which of the two it is and what to do about
 * it — the reason the state exists at all is that a missing bake used to surface
 * only as a console warning.
 *
 * Deliberately not a permanent chip: LightingStatusChip reports lighting, and
 * navmesh status lives on the outliner row's dot. This appears when the user
 * asked about navigation and the answer is "there isn't any".
 */
import { useEffect, useState } from 'react'
import { useRailInset } from '../hooks/useRailInset'
import { liveTuneStore } from '../lib/liveTuneStore'
import { subscribeBake, getBakeState } from '../lib/bakeStatusStore'
import { activeEnvAssetId } from '../lib/activeEnvironmentAsset'
import { rowBakeState } from '../lib/bakeKind'
import { navmeshRowStatus } from '../lib/navmeshStatus'

export default function NavmeshStatusChip({ selected = false }) {
  // Same left inset as LightingStatusChip, so the two line up when the docked
  // Outliner rail is open (ENG-895).
  const railInset = useRailInset()
  // window.__NAVMESH__ is not React state, and a bake changes it from outside
  // this tree, so tick while the chip is on screen (UI cadence, not per frame).
  const [, force] = useState(0)
  useEffect(() => subscribeBake(() => force(n => n + 1)), [])
  useEffect(() => {
    if (!selected) return
    const t = setInterval(() => force(n => n + 1), 800)
    return () => clearInterval(t)
  }, [selected])

  if (!selected) return null

  const hasEnvChip = !!liveTuneStore.getState().environment?.splatUrl
  const nav = typeof window !== 'undefined' ? window.__NAVMESH__ ?? null : null
  const assetId = activeEnvAssetId()
  const bake = assetId ? getBakeState(assetId) : null
  const bakeKind = bake
    ? rowBakeState({
      bakeable: true, baking: bake.status === 'baking', bakingKind: bake.kind ?? null,
      status: bake.status ?? 'none', ready: false, row: 'navmesh',
    })
    : null
  const st = navmeshRowStatus(nav, bakeKind ? { kind: bakeKind, pct: (bake?.progress ?? 0) * 100 } : null)

  // Built: the overlay is the answer, so no chip.
  if (st.built) return null

  return (
    <div
      data-testid="navmesh-status-chip"
      data-tone={st.tone}
      style={{
        // Stacked UNDER LightingStatusChip in the same top-left column. That chip
        // renders iff an environment is active, so the offset is derived from the
        // same condition rather than guessed — with no environment this chip
        // takes the top slot itself.
        position: 'absolute', top: hasEnvChip ? 84 : 46, left: 10 + railInset, zIndex: 20,
        display: 'flex', alignItems: 'center', gap: 8, maxWidth: 340,
        padding: '6px 10px',
        background: 'color-mix(in srgb, var(--m-panel) 92%, transparent)',
        border: `1px solid ${st.tone === 'error' ? 'var(--m-danger)' : 'var(--m-line-hi)'}`,
        borderRadius: 'var(--m-radius-sm)',
        fontFamily: 'var(--m-font)', fontSize: 10, color: 'var(--m-fg-2)',
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%', flex: 'none',
        background: st.tone === 'error' ? 'var(--m-danger)' : 'var(--m-warn)',
      }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          letterSpacing: '.06em', textTransform: 'uppercase',
          color: st.tone === 'error' ? 'var(--m-danger)' : 'var(--m-warn)',
        }}>{st.meta}</span>
        <span style={{ fontFamily: 'var(--m-mono)', fontSize: 9.5, color: 'var(--m-fg-3)' }}>
          {st.detail}
        </span>
      </span>
    </div>
  )
}
